import React, { useEffect, useState, useMemo } from "react";
import {
  Modal,
  Form,
  Row,
  Col,
  Select,
  Typography,
  message,
  Button,
  Divider,
  Tooltip,
  Tag,
  Spin,
  Table,
  Input,
} from "antd";
import moment from "moment";
import { platformApi } from "../../../../../api";
import VehicleName from "../../../../VehicleName";

const { Title } = Typography;

const AssignJob = ({
  selectedRamp,
  branch,
  refreshData,
  partsRows,
  setPartsRows,
  setJobSelected,
  open,
  setSelectedRamp,
}) => {
  const [jobNo, setJobNo] = useState([]);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [assigning, setAssigning] = useState(false);
  const [mechanics, setMechanics] = useState([]);
  const [loadingJobMaterials, setLoadingJobMaterials] = useState(false);
  const [existingMaterialIssueId, setExistingMaterialIssueId] = useState(null);
  const [initializing, setInitializing] = useState(true);

  // inline parts issue state
  const [partsOptions, setPartsOptions] = useState([]);
  const [currentSlipNo, setCurrentSlipNo] = useState(0);

  const handleAssign = () => {
    if (!selectedJob) {
      message.error("Select a job first");
      return;
    }
    setAssigning(true);
    platformApi
      .post(`/api/ramp/assign`, {
        rampId: selectedRamp.id,
        jobOrderId: selectedJob.id,
      })
      .then(() => {
        setAssigning(false);
        message.success("Job assigned to ramp");

        // Immediately update selectedRamp with job information
        setSelectedRamp((prevRamp) => ({
          ...prevRamp,
          job: selectedJob,
        }));

        refreshData();
        setJobSelected(selectedJob.id);
      })
      .catch((err) => {
        setAssigning(false);
        console.error("Assign error:", err);
        message.error("Failed to assign job");
      });
  };

  const handleReassignJob = () => handleAssign();

  const fetchJobMaterials = async (jobId) => {
    if (!jobId) return;
    setLoadingJobMaterials(true);
    try {
      const res = await platformApi.get(
        `/api/materialIssue/jobMaterial/${jobId}`
      );
      const { data } = res;
      if (data?.code === 200 && data?.response?.code === 200) {
        const mi = data.response.data;
        setExistingMaterialIssueId(mi?.id || null);
        // preload rows from existing issue
        if (mi?.parts?.length) {
          const rows = mi.parts.map((p) => ({
            id: p.id,
            part: p.part,
            quantity: p.quantity,
            mrp: p.part?.mrp,
            issueType: p.issueType || "PAID",
          }));
          setPartsRows(rows);
          setCurrentSlipNo(mi?.slipNumber || 0);
        }
      } else {
        setExistingMaterialIssueId(null);
        setPartsRows([
          {
            id: `row-${Date.now()}`,
            part: null,
            quantity: "",
            mrp: "",
            issueType: "PAID",
          },
        ]);
      }
    } catch (e) {
      setExistingMaterialIssueId(null);
      setPartsRows([
        {
          id: `row-${Date.now()}`,
          part: null,
          quantity: "",
          mrp: "",
          issueType: "PAID",
        },
      ]);
    } finally {
      setLoadingJobMaterials(false);
    }
  };

  const fetchPartsOptions = async (branchId) => {
    try {
      const res = await platformApi.post(`/api/sparesInventory/branch/get`, {
        page: 1,
        size: 100,
        searchString: "",
        ...(branchId ? { branch: [branchId] } : {}),
      });
      const parts = [];
      if (res?.data?.response?.data) {
        for (const item of res.data.response.data) {
          if (item.phyQuantity > 0) parts.push(item.partNo);
        }
      }
      setPartsOptions(parts);
    } catch (err) {
      console.error("An error occured");
    }
  };


  const getMechanics = () => {
    return platformApi
      .get("/api/user")
      .then((result) => {
        let { data } = result;
        if (data.code === 200) {
          let list = data.data.users.filter((item) => {
            return (
              item.profile &&
              item.profile.department.role === "Mechanic" &&
              item.status === true
            );
          });
          setMechanics(list);
        }
      })
      .catch((err) => {
        console.error("Error getting mechanic", err);
      });
  };

  useEffect(() => {
    if (open) {
      getMechanics();
    }

    return () => {
      setJobSelected("");
    };
  }, [open]);

  useEffect(() => {
    if (selectedJob) {
      setJobSelected(selectedJob.id);
    }
  }, [selectedJob]);

  useEffect(() => {
    setInitializing(true);
    if (!selectedRamp) return;
    if (selectedRamp?.job) {
      setSelectedJobId(selectedRamp.job.id);
      setJobSelected(selectedRamp.job.id);
      setSelectedJob(selectedRamp.job);
    }
    Promise.all([getJobOptions()]).then(async () => {
      if (selectedRamp?.job?.id) {
        await fetchPartsOptions(selectedRamp.job?.branch?.id);
        await fetchJobMaterials(selectedRamp.job.id);
        // await loadSlipNumber(selectedRamp.job.id);
      }
      setInitializing(false);
    });
  }, [selectedRamp]);

  const getJobOptions = () => {
    const filter = { mechanic: [] };
    const branchIds = selectedRamp?.branch?.id
      ? [selectedRamp.branch.id]
      : undefined;
    const payload = {
      page: 1,
      size: 50,
      searchString: "",
      branch: branchIds,
      status: "ALL",
      filter,
    };
    return platformApi.post("/api/jobOrder/getPendingInProgress/", payload).then((res) => {
      const resp = res.data && res.data.response.data;
      setJobNo(resp.jobOrder);
      if (selectedRamp?.job?.id) {
        const job =
          resp.jobOrder.find((j) => j.id === selectedRamp.job.id) ||
          selectedRamp.job;
        setSelectedJob(job);
      }
    });
  };

  const searchJobNo = (value) => {
    const payload = {
      page: 1,
      size: 50,
      searchString: value,
      column: "jobNo",
      module: "jobOrders",
      branch: [selectedRamp?.branch?.id],
      except:  [ "Gate Pass","Payment Received","Invoice","Proforma Invoice","PAID"]
    };
    platformApi.post("/api/options/get/", payload).then((res) => {
      setJobNo(res.data.response);
    });
  };

  const onSelectJob = async (value) => {
    setSelectedJobId(value);
    setJobSelected(value);
    const job = jobNo && jobNo.find((j) => j.id === value);
    setSelectedJob(job || null);
    setExistingMaterialIssueId(null);
    await fetchPartsOptions(job?.branch?.id);
    await fetchJobMaterials(value);
  };

  const getCustomer = (job) => {
    if (!job) return {};
    if (job.customer) return job.customer;
    const nested =
      job.vehicle &&
      job.vehicle.customer &&
      job.vehicle.customer[0] &&
      job.vehicle.customer[0].customer;
    return nested || {};
  };

  return (
    <div
      style={{
        borderTop: "1px solid #ddd",
        paddingTop: "1rem",
        // marginTop: "1rem",
        backgroundColor: "#fafafa",
        borderRadius: "8px",
        padding: "1rem",
      }}
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <Title level={4}>Ramp {selectedRamp.rampNo}</Title>
      <Spin spinning={initializing}>
        <Form layout="vertical">
          <Form.Item label="Select Job">
            <Tooltip
              title={!selectedRamp?.mechanic ? "Assign mechanic before select a job" : ""}
              placement="topLeft"
            >
            <Select
              showSearch
              placeholder="Search by Job No"
              value={selectedJobId}
              onSearch={searchJobNo}
              onChange={onSelectJob}
              filterOption={false}
              disabled={!selectedRamp?.mechanic}
            >
              {jobNo &&
                jobNo?.map((job) => (
                  <Select.Option key={job.id} value={job.id}>
                    {job.jobNo}
                  </Select.Option>
                ))}
            </Select>
            </Tooltip>
          </Form.Item>

          {selectedJob && (
            <div style={{ marginTop: 0 }}>
              <Row gutter={12}>
                <Col span={12}>
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <strong>Job</strong>:{" "}
                    <Tag color="geekblue">{selectedJob.jobNo}</Tag>
                    <Button onClick={handleReassignJob} loading={assigning}>
                      Reassign Job
                    </Button>
                  </div>
                  <div>
                    <strong>Branch</strong>:{" "}
                    {selectedJob.branch && selectedJob.branch.name}
                  </div>
                  <div>
                    <strong>Model</strong>:{" "}
                    <VehicleName nameOnly value={selectedJob.vehicle?.vehicle} />
                  </div>
                  <div>
                    <strong>Chassis No</strong>:{" "}
                    {selectedJob.vehicle && selectedJob.vehicle.chassisNo}
                  </div>
                  <div>
                    <strong>Register No</strong>:{" "}
                    {selectedJob.vehicle && selectedJob.vehicle.registerNo}
                  </div>
                  <div>
                    <strong>Engine No</strong>:{" "}
                    {selectedJob.vehicle && selectedJob.vehicle.engineNo}
                  </div>
                  <div>
                    <strong>Customer</strong>: {getCustomer(selectedJob)?.name}
                  </div>
                </Col>
              </Row>
            </div>
          )}
        </Form>
      </Spin>
    </div>
  );
};

export default AssignJob;
